import { useTranslation } from 'react-i18next';

import { certifiedCapabilitiesFor, isCehrt, isTier } from '@emr/shared';

import { useSession } from '../auth/sessionContext.ts';
import { HelpLink } from '../components/HelpLink.tsx';
import { DetailRow, Panel } from '../components/primitives.tsx';

/**
 * What this session actually is.
 *
 * Every fact here is derived from `@emr/shared` rather than restated. `isCehrt` in particular
 * exists so that marketing copy and an in-app plan comparison derive the answer instead of
 * asserting it — misrepresenting certification is the exact fact pattern behind four separate
 * False Claims Act settlements in this market, and a hardcoded "Certified ✓" in a component is how
 * a claim like that gets made by accident.
 */
export function OverviewPage() {
  const { t } = useTranslation('overview');
  const session = useSession();
  const { tier } = session;
  const certified = certifiedCapabilitiesFor(tier);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>

      <Panel title={t('session.panelTitle')}>
        <dl className="m-0">
          <DetailRow label={t('session.email')}>{session.email}</DetailRow>
          <DetailRow label={t('session.practice')}>{session.orgName}</DetailRow>
          <DetailRow label={t('session.role')}>{session.role}</DetailRow>
          <DetailRow label={t('session.platformOperator')}>
            {session.isPlatformOperator ? t('session.yes') : t('session.no')}
          </DetailRow>
        </dl>
      </Panel>

      <Panel
        title={
          <>
            {t('plan.panelTitle')}
            <HelpLink topicId="plans-and-upgrades" />
          </>
        }
      >
        <dl className="m-0">
          <DetailRow label={t('plan.tier')}>
            {isTier(tier) ? (
              tier
            ) : (
              // An unrecognised tier is corrupt data or a half-finished migration. `isEntitled`
              // already fails closed on it; saying so beats rendering the raw value as if it were
              // a plan someone could be on.
              <span className="text-danger">{t('plan.unrecognisedTier', { tier })}</span>
            )}
          </DetailRow>
          <DetailRow label={t('plan.certifiedTechnology')}>
            {isCehrt(tier) ? t('plan.certifiedYes') : t('plan.certifiedNo')}
          </DetailRow>
          <DetailRow label={t('plan.certifiedCapabilities')}>
            {certified.length === 0 ? (
              <span className="text-ink-muted">{t('plan.noCertifiedCapabilities')}</span>
            ) : (
              <ul className="m-0 list-none p-0">
                {certified.map((capability) => (
                  <li key={capability} className="font-mono text-sm">
                    {capability}
                  </li>
                ))}
              </ul>
            )}
          </DetailRow>
        </dl>
      </Panel>
    </div>
  );
}
